import { ReactElement, useCallback, useEffect, useMemo } from 'react'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { Button, Stack, Typography } from '@mui/material'
import { IFCAlignmentSection } from '@editor/components'
import { IfcPropertiesView } from '@editor/components/IFCElementForm/components'
import { CorneredSection } from '@ui2/structure'
import { useIfcStore } from '@stores'
import { useControlStore } from '@editorStores'
import { useIfcFiles, useSceneElement, useSelectionMode } from '@editorHooks'
import { CONFIGURE_TRANSFORMATION_SELECTION_KEY } from '../../constants'
import useApplyIfcAlignment from '../../hooks/useApplyIfcAlignment'
import useIFCElementSelection from '../../hooks/useIFCElementSelection'
import IFCAlignmentScene from '../IFCAlignmentScene'
import IFCSelectionInfo from '../IFCSelectionInfo'

const EMPTY_PROPS = {}

/**
 * Alignment step of the IFC importer. Offers two ways to align the model:
 *  - via the scene: pick three points (origin + two axes) and apply the resulting transformation,
 *  - via the text fields: the shared IFCAlignmentForm (reference points + z-rotation).
 * While the scene alignment is being defined, the text fields are disabled.
 * Elements can be selected (click or marquee, paused while picking points) and hidden, so the
 * automatic alignment — which only considers visible elements — can be scoped.
 */
const IFCAlignmentPanel = (): ReactElement => {
  const activeIfcFileGuid = useControlStore(state => state.activeIfcFileGuid)
  const setAxesEnabled = useControlStore(state => state.setAxesEnabled)
  const setApplyIfcAlignmentConfig = useControlStore(state => state.setApplyIfcAlignmentConfig)

  // Render the point-picking tool into the scene while this panel is open.
  useSceneElement({ enabled: true, properties: EMPTY_PROPS, component: IFCAlignmentScene })

  // Show the global coordinate system in the scene while the alignment step is open, and hide it
  // again when leaving the step (on unmount).
  useEffect(() => {
    setAxesEnabled(true)
    return () => setAxesEnabled(false)
  }, [setAxesEnabled])

  // Leaving the alignment step re-enables the applied alignment, so the model is never left in its
  // raw pose after picking was started but not finished.
  useEffect(() => {
    return () => setApplyIfcAlignmentConfig(true)
  }, [setApplyIfcAlignmentConfig])

  const glbFiles = useIfcFiles()
  const activeFile = useMemo(
    () =>
      activeIfcFileGuid
        ? glbFiles.find((f: FileItem) => f.guid === activeIfcFileGuid) ?? null
        : null,
    [activeIfcFileGuid, glbFiles],
  )

  const firstPoint = useIfcStore(state => state.firstPoint)
  const resetPoints = useIfcStore(state => state.resetPoints)

  const { setSelectionMode, unsetSelectionMode, selectionModeKey } = useSelectionMode()
  const isPicking = selectionModeKey === CONFIGURE_TRANSFORMATION_SELECTION_KEY
  // Disable the manual text fields while an alignment is being defined in the scene.
  const sceneAligning = isPicking || !!firstPoint

  // Element selection for hiding elements (pause it while the alignment points are being picked,
  // so scene clicks go to the point picker only).
  const { selectedIfcElementGuids, setSelectedIfcElementGuids, metadata } = useIFCElementSelection({
    enabled: !sceneAligning,
  })
  const hiddenElementIds = useControlStore(state => state.hiddenElementIds)
  const addHiddenElementIds = useControlStore(state => state.addHiddenElementIds)
  const removeHiddenElementIds = useControlStore(state => state.removeHiddenElementIds)

  const handleHideSelection = useCallback(() => {
    addHiddenElementIds(selectedIfcElementGuids)
    setSelectedIfcElementGuids([])
  }, [addHiddenElementIds, selectedIfcElementGuids, setSelectedIfcElementGuids])

  const handleShowAllHidden = useCallback(() => {
    removeHiddenElementIds(Array.from(hiddenElementIds))
  }, [removeHiddenElementIds, hiddenElementIds])

  const { applyAlignment, resetAlignment, canApply } = useApplyIfcAlignment()

  const handleConfigure = useCallback(() => {
    // Show the raw (unaligned) model while picking the three points so they land on the original
    // geometry. The applied alignment is restored once picking finishes or is cancelled.
    setApplyIfcAlignmentConfig(false)
    resetPoints()
    setSelectionMode({
      key: CONFIGURE_TRANSFORMATION_SELECTION_KEY,
      message: 'Drei Punkte im Modell abgreifen: Ursprung, X-Achse, Y-Achse',
    })
  }, [setApplyIfcAlignmentConfig, resetPoints, setSelectionMode])

  const handleCancel = useCallback(() => {
    resetPoints()
    if (isPicking) unsetSelectionMode()
    setApplyIfcAlignmentConfig(true)
  }, [resetPoints, isPicking, unsetSelectionMode, setApplyIfcAlignmentConfig])

  // Scene point-picking actions, injected into the alignment form between the camera-view select
  // and the "Ausrichtung anwenden" switch. Kept enabled while picking (unlike the manual fields).
  const alignmentActions = (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Hier haben Sie die Möglichkeit, die Konfiguration des Koordinatensystems über eine
        Interaktion in der Scene vorzunehmen. Springen Sie ins Tab {"'"}Erweitert{"'"}, um die
        Konfiguration manuell vorzunehmen
      </Typography>

      {!sceneAligning && (
        <>
          <Button
            variant="contained"
            size="small"
            onClick={handleConfigure}
            disabled={!activeFile}
            data-cy="btn-ifc-pick-alignment"
          >
            Ausrichtung im Modell abgreifen
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={resetAlignment}
            disabled={!activeFile}
            data-cy="btn-ifc-reset-alignment"
          >
            Zurücksetzen
          </Button>
        </>
      )}
      {canApply && (
        <Button variant="contained" size="small" onClick={applyAlignment}>
          Transformation anwenden
        </Button>
      )}
      {sceneAligning && (
        <Button variant="text" size="small" onClick={handleCancel}>
          Abbrechen
        </Button>
      )}
    </Stack>
  )

  return (
    <Stack spacing={2}>
      <CorneredSection title="Auswahl">
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Elemente per Klick oder Rahmenauswahl markieren und ausblenden — ausgeblendete Elemente
            werden bei der automatischen Ausrichtung nicht berücksichtigt.
          </Typography>

          <IFCSelectionInfo showWhenEmpty />

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityOffIcon />}
              onClick={handleHideSelection}
              disabled={selectedIfcElementGuids.length === 0}
              data-cy="btn-ifc-alignment-hide-selection"
            >
              Auswahl ausblenden
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={handleShowAllHidden}
              disabled={hiddenElementIds.size === 0}
              data-cy="btn-ifc-alignment-show-hidden"
            >
              Alle einblenden
            </Button>
          </Stack>
        </Stack>
      </CorneredSection>

      <IFCAlignmentSection
        alignmentActions={alignmentActions}
        alignmentFieldsDisabled={sceneAligning}
      />

      {selectedIfcElementGuids.length > 0 && (
        <CorneredSection title="Eigenschaften" collapsible defaultOpen>
          <IfcPropertiesView
            guids={selectedIfcElementGuids}
            metadata={metadata}
            onSelectGuids={setSelectedIfcElementGuids}
          />
        </CorneredSection>
      )}
    </Stack>
  )
}

export default IFCAlignmentPanel
